/**
 * Экран черновика. Собирает режимы экрана и владеет их данными — панели в сеть не ходят.
 *
 * ТРИ РЕЖИМА, И ПО УМОЛЧАНИЮ — ЧТЕНИЕ (решение заказчика 26.08.2026). Раньше клик по посту в
 * бэклоге сразу открывал редактор разметки: человеку показывали инструмент правки прежде, чем
 * он успел прочитать, о чём пост и откуда он взялся. Порядок восстановлен:
 *
 *   `read`    — тема и первоисточник, затем превью «как увидит подписчик». Редактора на
 *               экране нет вовсе: он появляется по кнопке, а не встречает при входе.
 *   `edit`    — слева разметка, справа живое превью. Та самая пара, что была раньше; она
 *               сохранена целиком, у неё изменился только вход.
 *   `compare` — два полотна превью рядом: слева версия-база с зачёркнутым убранным, справа
 *               версия-цель с выделенным добавленным.
 *
 * Почему сборка живёт здесь, а не внутри панелей: модель рендера одна на весь экран, и после
 * любого решения меняется сразу всё — исходник, превью, набор кнопок, строка списка слева.
 * Один владелец данных значит один refetch и невозможность показать превью новой версии
 * рядом с кнопками старой.
 *
 * Разбор ответа схемой (RenderModelSchema) здесь обязателен, а не «на всякий случай»:
 * 09 §9.1 требует, чтобы версия, не сошедшаяся со схемой, выключала ВСЕ кнопки решения —
 * это версия, посчитанная другим алгоритмом канонизации, и одобрять её нельзя. Версия-база
 * сравнения разбирается тем же способом и по той же причине.
 *
 * ЧЕРНОВИК ОТКРЫВАЕТСЯ В СВОЁМ БРЕНДЕ (2026-10-25, изоляция брендов). Черновик читается по id, а
 * бренд адреса — то, что набрали или переслали: `/b/geoboost/draft/<id черновика AI Lab>`
 * показывал бы пост AI Lab в шапке, теме, режиме и канале GeoBoost, и человек одобрял бы его,
 * глядя на чужой канал. Поэтому бренд черновика, отличный от адреса, — переход на адрес бренда
 * черновика с заменой истории (`Redirect replace`): ключ `Switch` в `App.tsx` — бренд адреса, и
 * экран пересоздаётся целиком, уже в своей теме. Полоса решения бренда пропом не получает вовсе
 * — она берёт `model.brand` (`DecisionPanelProps`). Шапка черновика и модель версии, назвавшие
 * разные бренды, — ответ сломанного бэкенда: экран говорит это, а решений не показывает.
 */

import { DRAFT_TRANSITIONS, type DraftState } from '@smm/contracts';
import { postLabel } from '@smm/contracts/schemas';
import { type JSX, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Redirect, useLocation } from 'wouter';
import {
  type DraftWithVersions,
  type RenderModel,
  RenderModelSchema,
  TransportPolicySchema,
} from '../api/types.ts';
import { useResource } from '../api/useResource.ts';
import { brandBannedOrgs, useBrands } from '../brand.tsx';
import { AutoApprovalBadge } from '../components/AutoApprovalBadge.tsx';
import { AutoBadge } from '../components/AutoBadge.tsx';
import { StatusPill } from '../components/StatusPill.tsx';
import { useShellSettings } from '../components/shellSettings.ts';
import { styleMetaOf } from '../components/styleVersions.ts';
import { VersionDiff, type VersionDiffResult } from '../components/VersionDiff.tsx';
import { PreviewPanel } from '../preview/PreviewPanel.tsx';
import { validateDraft } from '../preview/validation.ts';
import { bannedOrgGate } from './bannedOrgs.ts';
import type { DraftPanelProps, TypedContent, UiIssue } from './contracts.ts';
import styles from './DraftPanel.module.css';
import { DecisionPanel } from './decisions/DecisionPanel.tsx';
import { SourceEditorPanel } from './editor/SourceEditorPanel.tsx';
import { OpinionStrip } from './opinion/OpinionStrip.tsx';
import { TopicPanel } from './TopicPanel.tsx';
import { DocumentStrip } from './transport/DocumentStrip.tsx';
import { RichConvertAction } from './transport/RichConvertAction.tsx';
import { useDocumentUpload } from './transport/useDocumentUpload.ts';
import { lockReason, PICTURE_OBJECT } from './versionLock.ts';
import { useVisualUpload } from './visual/useVisualUpload.ts';
import { VisualStrip } from './visual/VisualStrip.tsx';
import {
  proposedMediaOf,
  proposedRefOf,
  visualIssues,
  visualViewOf,
} from './visual/visualState.ts';

/** Ошибка разбора модели — блокирующая проблема уровня 1 (09 §9.1), с той самой фразой. */
const SCHEMA_ISSUE: UiIssue = {
  level: 'blocking',
  code: 'render_model_invalid',
  message:
    'Версия черновика не соответствует текущему алгоритму канонизации — обратитесь к инженеру.',
};

/**
 * Состояния, из которых версия вытесняется новой, — выведены из рёбер контракта, а не
 * переписаны списком: набор обязан совпадать с `SUPERSEDABLE` бэкенда.
 */
const SUPERSEDABLE: ReadonlySet<DraftState> = new Set(
  DRAFT_TRANSITIONS.filter((t) => t.to === 'superseded').map((t) => t.from),
);

/** Заглушка контента, пока модель рендера не разобрана: загрузка при ней выключена. */
const EMPTY_TYPED: TypedContent = { text: '', entities: [] };

type ScreenMode = 'read' | 'edit' | 'compare';

/**
 * С какой версией сравнивать по умолчанию: ближайшая предыдущая, а при её отсутствии —
 * ближайшая следующая. Тот же выбор делает бэкенд, когда `against` не передан (n−1), и
 * расходиться с ним нельзя: иначе кнопка и адрес запроса означали бы разное.
 */
export function pickAgainst(others: readonly number[], shown: number): number | null {
  const lower = others.filter((n) => n < shown);
  if (lower.length > 0) return Math.max(...lower);
  const higher = others.filter((n) => n > shown);
  return higher.length > 0 ? Math.min(...higher) : null;
}

/** Форма кода бренда в адресе — `BRAND_RE` контрактов, как у провайдера бренда. */
const BRAND_ID_RE = /^[a-z][a-z0-9_-]{1,31}$/;

/**
 * Адрес черновика в его собственном бренде; версия из адреса сохраняется. `null` — код бренда
 * не годится в адрес (ответ бэкенда, которого не бывает): переходить туда нельзя.
 */
export function ownBrandPath(
  draftBrand: string,
  draftId: string,
  versionNo: number | null,
): string | null {
  if (!BRAND_ID_RE.test(draftBrand)) return null;
  const base = `/b/${draftBrand}/draft/${encodeURIComponent(draftId)}`;
  return versionNo === null ? base : `${base}/v/${String(versionNo)}`;
}

export function DraftPanel({
  brand,
  draftId,
  versionNo,
  onChanged,
  auto = false,
}: DraftPanelProps): JSX.Element {
  const draft = useResource<DraftWithVersions>(`/api/v1/drafts/${draftId}`);
  const [, navigate] = useLocation();
  /**
   * Политика бренда «все посты — расширенным сообщением» (docs/release-2026-09-19.md §9.3).
   * Нужна экрану ради НЕСОХРАНЁННОГО: правка в редакторе сохранится по ней, и её превью,
   * плашка карточки и предсохранная заморозка обязаны знать это до сохранения; сохранённая
   * версия несёт свой `method` и в политике не нуждается. Её же ждёт «Перевести в расширенное».
   *
   * Меняют политику в «Настройках» шапки — экран перечитывает её по счётчику `rev`
   * (`shellSettings.ts`), как редактор стилей перечитывает умолчание. Ответ разбирается схемой:
   * чужой ответ значит «неизвестна» (`undefined`), а не догадку.
   */
  const policyRes = useResource<unknown>(
    `/api/v1/transport-policy?brand=${encodeURIComponent(brand)}`,
  );
  const { refetch: refetchPolicy } = policyRes;
  const { rev } = useShellSettings();
  const seenRev = useRef(rev);
  useEffect(() => {
    if (seenRev.current === rev) return;
    seenRev.current = rev;
    refetchPolicy();
  }, [rev, refetchPolicy]);
  const policyRich = useMemo((): boolean | undefined => {
    const r = TransportPolicySchema.safeParse(policyRes.data);
    return r.success ? r.data.rich_always : undefined;
  }, [policyRes.data]);
  const [screen, setScreen] = useState<ScreenMode>('read');
  const [compareTo, setCompareTo] = useState<number | null>(null);
  /**
   * Набранная, ещё не сохранённая правка. Живёт здесь, а не в редакторе, ровно потому, что её
   * потребитель — соседняя колонка: это и есть «живое превью справа» из решения Ильи 22.08.
   * Решения при этом продолжают опираться на model.canonical — ключ выпускается под
   * сохранённую версию, и превью на это влиять не имеет права.
   */
  const [typed, setTyped] = useState<TypedContent | null>(null);
  /**
   * Показывать ли картинку-кандидата в пузыре. По умолчанию ВКЛЮЧЕНО: решение «принять или
   * отклонить» принимают, глядя на пост с картинкой, а не на миниатюру рядом. Ровно поэтому
   * гейт визуала обязан работать безотказно — человек, смотрящий на пузырь с предложением,
   * подписал бы пост БЕЗ него, потому что кандидат в `content_hash` не входит.
   */
  const [showProposal, setShowProposal] = useState(true);

  // Смена черновика или версии возвращает экран в чтение. Правка состояния во время рендера,
  // а не в эффекте: иначе один кадр показывал бы редактор уже другого поста.
  const opened = `${draftId}:${String(versionNo)}`;
  const [openedFor, setOpenedFor] = useState(opened);
  if (openedFor !== opened) {
    setOpenedFor(opened);
    setScreen('read');
    setCompareTo(null);
    setTyped(null);
    setShowProposal(true);
  }

  const current = draft.data?.draft.current_version_id;
  const versions = draft.data?.versions ?? [];
  // Номер версии: из URL, иначе последняя. `current_version_id` — идентификатор, а не номер,
  // поэтому номер берём из списка версий, а не пытаемся вывести из него.
  const shown = versionNo ?? (versions.length === 0 ? null : (versions.at(-1)?.version_no ?? null));

  const model = useResource<unknown>(
    shown === null ? null : `/api/v1/drafts/${draftId}/versions/${String(shown)}`,
  );
  const comparing = screen === 'compare' && shown !== null && compareTo !== null;
  const baseModel = useResource<unknown>(
    comparing ? `/api/v1/drafts/${draftId}/versions/${String(compareTo)}` : null,
  );
  const diff = useResource<VersionDiffResult>(
    comparing
      ? `/api/v1/drafts/${draftId}/versions/${String(shown)}/diff?against=${String(compareTo)}`
      : null,
  );

  const parsed = useMemo(() => {
    if (model.data === undefined) return null;
    const r = RenderModelSchema.safeParse(model.data);
    return r.success ? { ok: true as const, model: r.data } : { ok: false as const };
  }, [model.data]);

  const parsedBase = useMemo(() => {
    if (baseModel.data === undefined) return null;
    const r = RenderModelSchema.safeParse(baseModel.data);
    return r.success ? r.data : null;
  }, [baseModel.data]);

  /**
   * Порядок именно такой: сначала правила Telegram, потом гейты — визуала и пометки
   * организаций, запрещённых в РФ (`bannedOrgs.ts`). Блокирующие перечисляются кодами в строке
   * «Блокирует утверждение», и правила провода обязаны стоять первыми — они говорят «пост не
   * уйдёт», а гейты говорят «решение не принято» и «одобрение не пропустит». Разметка — флаг
   * бренда ВЕРСИИ (`model.brand`), как всё, что решает полоса решения.
   */
  const brands = useBrands();
  const issues: readonly UiIssue[] = useMemo(() => {
    if (parsed === null) return [];
    if (!parsed.ok) return [SCHEMA_ISSUE];
    return [
      ...validateDraft(parsed.model.canonical, { richAlways: policyRich }),
      ...visualIssues(parsed.model),
      ...bannedOrgGate(parsed.model, brandBannedOrgs(brands, parsed.model.brand)),
    ];
  }, [parsed, policyRich, brands]);

  /**
   * Экран жив. `WorkspacePage` пересоздаёт панель на каждый переход к другому посту
   * (`key={draftId}`), и ответ, пришедший позже, принадлежит уже закрытому экрану: переход по
   * адресу его версии увёл бы человека с поста, который он только что открыл (ревью, ui-1).
   */
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Перечитать экран, НЕ трогая набранную правку: черновик (по его списку версий экран узнаёт,
   * что текущей стала другая) и модель показанной версии. Это исходы, после которых набранное
   * по-прежнему правка показанной версии: действие с файлом новой версии не родило (отмена,
   * тот же файл, файла уже нет) или закрылась тестовая отправка. Сбрасывать здесь правку
   * значило бы молча её потерять: редактор держит текст, но заново его не сообщает, и «Готово»
   * выпускало бы из правки без вопроса (ревью, ui-2). Список слева — забота вызывающего.
   */
  const reread = useCallback((): void => {
    draft.refetch();
    model.refetch();
  }, [draft.refetch, model.refetch]);

  /**
   * Шапка сообщила «настройки изменились» (`rev`) — перечитать и пост. Ради выключателя
   * автоодобрения (docs/ai-lab.md): выключение возвращает автоодобренные посты из слота на
   * согласование, и открытый экран иначе показывал бы «запланирован» у поста, который уже
   * «ожидает». Набранная правка не сбрасывается (`reread`). Прочие смены настроек пост не
   * меняют, и лишний GET здесь дешевле второго счётчика в шапке.
   */
  const seenRevForPost = useRef(rev);
  useEffect(() => {
    if (seenRevForPost.current === rev) return;
    seenRevForPost.current = rev;
    reread();
  }, [rev, reread]);

  const refetchAll = useCallback((): void => {
    setTyped(null);
    reread();
    onChanged();
  }, [reread, onChanged]);

  /** Действие с файлом новой версии не родило: перечитать, набранное оставить (`reread`). */
  const onDocumentUnchanged = useCallback((): void => {
    reread();
    onChanged();
  }, [reread, onChanged]);

  /**
   * Мнение автора ушло на переделку (полоса «Мнение автора»). Версии это не родило — её родит
   * писатель, — поэтому набранная правка остаётся (`reread`): она по-прежнему правка показанной
   * версии, и молча терять её нельзя. Список слева узнаёт о новом состоянии поста.
   */
  const onOpinionRequested = useCallback((): void => {
    reread();
    onChanged();
  }, [reread, onChanged]);

  /**
   * Действие родило новую версию — файл прикреплён или снят, пост переведён в расширенное —
   * и экран обязан показать ЕЁ. По адресу без номера версии это делает перечитывание: текущей
   * становится последняя. Адрес с номером (`/v/:n`) закрепляет показанную версию, и без
   * перехода экран остался бы на заменённой — поэтому туда уходим явно. `null` — номер
   * неизвестен или экран устарел: просто перечитать. Политика перечитывается тоже: «перевод»
   * без новой версии значит, что её успели выключить.
   */
  const onVersionCreated = useCallback(
    (n: number | null): void => {
      // Экрана этого поста уже нет: переходить некуда и перечитывать нечего, а список слева —
      // общий, и об исходе он узнать обязан.
      if (!mounted.current) {
        onChanged();
        return;
      }
      if (n !== null && versionNo !== null && n !== versionNo) {
        navigate(
          `/b/${encodeURIComponent(brand)}/draft/${encodeURIComponent(draftId)}/v/${String(n)}`,
        );
      }
      refetchPolicy();
      refetchAll();
    },
    [brand, draftId, navigate, onChanged, refetchAll, refetchPolicy, versionNo],
  );

  /**
   * Загрузка картинки — ОДНО состояние на кнопку и на перетаскивание, и владеет им экран, а
   * не полоса: зона сброса — вся колонка поста, включая пузырь превью, а полоса лежит внутри
   * неё. Хук зовётся до ранних возвратов, потому что порядок хуков обязан быть постоянным;
   * до разбора модели он выключен (`editable: false`) и в сеть не ходит.
   */
  const loaded = parsed?.ok === true ? parsed.model : null;
  const loadedEditable = loaded?.status !== undefined && SUPERSEDABLE.has(loaded.status);
  const upload = useVisualUpload({
    draftId,
    versionNo: loaded?.version_no ?? 0,
    canonical: loaded?.canonical ?? EMPTY_TYPED,
    editable: loadedEditable,
    lockedBecause:
      loaded === null || loadedEditable ? null : lockReason(loaded.status, PICTURE_OBJECT),
    onDone: refetchAll,
  });
  // PDF внизу поста (§9.2) — та же схема владения, что у картинки: хук экрана, полоса рисует.
  const documentUpload = useDocumentUpload({
    draftId,
    versionNo: loaded?.version_no ?? 0,
    contentHash: loaded?.content_hash ?? '',
    status: loaded?.status ?? null,
    onDone: onVersionCreated,
    onUnchanged: onDocumentUnchanged,
  });

  if (draft.error !== undefined) {
    return (
      <div className={styles['panel']}>
        <div className="val val-err">Черновик не открылся: {draft.error.message}</div>
      </div>
    );
  }
  // Черновик чужого бренда — на адрес его бренда, до первой отрисовки поста (шапка файла).
  const draftBrand = draft.data?.draft.brand;
  if (draftBrand !== undefined && draftBrand !== brand) {
    const target = ownBrandPath(draftBrand, draftId, versionNo);
    if (target === null) {
      return (
        <div className={styles['panel']}>
          <div className="val val-err" role="alert">
            Черновик принадлежит бренду с непригодным кодом («{draftBrand}») — открыть его в своём
            бренде нельзя, а в чужом решения по нему не принимаются.
          </div>
        </div>
      );
    }
    return <Redirect to={target} replace />;
  }
  // Отказ ЗАПРОСА ВЕРСИИ — до «загружаем»: иначе человек смотрит на вечное «Загружаем
  // черновик…» и не знает ни что версия не открылась, ни почему (F-087).
  if (model.error !== undefined) {
    return (
      <div className={styles['panel']}>
        <div className="val val-err">Версия не открылась: {model.error.message}</div>
      </div>
    );
  }
  if (parsed === null || draft.data === undefined) {
    return (
      <div className={styles['panel']}>
        <div className="micro">Загружаем черновик…</div>
      </div>
    );
  }
  if (!parsed.ok) {
    return (
      <div className={styles['panel']}>
        <div className="val val-err">{SCHEMA_ISSUE.message}</div>
        <div className="micro">
          Все действия по этой версии выключены намеренно: она посчитана другим алгоритмом
          канонизации, и подписывать её нельзя.
        </div>
      </div>
    );
  }

  const rm: RenderModel = parsed.model;
  if (rm.brand !== brand) {
    // Шапка черновика уже совпала с адресом (иначе был бы переход выше), а версия называет
    // другой бренд. Переходить некуда — шапка вернула бы обратно; решать по такому посту нельзя.
    return (
      <div className={styles['panel']}>
        <div className="val val-err" role="alert">
          Черновик и его версия называют разные бренды ({brand} и {rm.brand}) — это ответ
          неисправного бэкенда. Все действия по посту выключены: неизвестно, в какой канал он уйдёт.
        </div>
      </div>
    );
  }
  /**
   * Набранная правка действует, пока показанную версию можно заменить. Её могли заменить или
   * поставить в слот под открытым редактором (соседняя вкладка; перечитывание без сброса
   * правки — `reread` — это покажет), и тогда редактор рисует сохранённый текст без
   * «Сохранить»: набранное сохранить уже некуда. Превью, «Готово» и «Перевести в
   * расширенное» не держатся за правку, которой на экране нет, — иначе «сохраните или
   * отмените правку» запирало бы человека в правке без обоих способов её закончить.
   */
  const liveTyped = SUPERSEDABLE.has(rm.status) ? typed : null;
  const head = draft.data.draft;
  const others = versions.map((v) => v.version_no).filter((n) => n !== rm.version_no);
  /**
   * Знак поста для переписки — «post #N», сквозной номер поста в бренде (`PostNo` в
   * контрактах). До 10.09.2026 здесь стояли восемь знаков UUID («news #01a08797»): сквозного
   * номера тогда не существовало. Полный идентификатор по-прежнему висит `title` на
   * заголовке, откуда его копируют.
   */
  const label = postLabel(rm.post_no);
  const styleMeta = styleMetaOf(rm.style, rm.comparison);
  const headMeta: readonly string[] = [
    ...(versions.length < 2 ? [] : [`v${String(rm.version_no)} из ${String(versions.length)}`]),
    ...(styleMeta === null ? [] : [styleMeta]),
  ];

  /**
   * Левая группа полосы действий — переключение режима. Она стоит в одной полосе с решениями
   * и слева от них: «что я делаю с постом» и «что я решаю по посту» — разные вопросы, и рука
   * не должна попадать в утверждение, целясь в «Редактировать».
   */
  const modeControls = (): JSX.Element => {
    if (screen === 'edit') {
      return (
        <>
          <button
            type="button"
            className={liveTyped === null ? 'btn btn-ghost btn-sm' : 'btn btn-sm btn-dis'}
            disabled={liveTyped !== null}
            onClick={() => {
              setScreen('read');
            }}
          >
            Готово
          </button>
          {/* Выход из правки с несохранённым текстом потерял бы его молча: редактор
              пересоздаётся из сохранённой версии. Поэтому выход ждёт решения по правке —
              оба способа закончить её стоят тут же, в колонке исходника. */}
          {liveTyped === null ? null : <span className="micro">сохраните или отмените правку</span>}
        </>
      );
    }
    if (screen === 'compare') {
      return (
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setScreen('read');
          }}
        >
          Закрыть сравнение
        </button>
      );
    }
    return (
      <>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => {
            setScreen('edit');
          }}
        >
          Редактировать
        </button>
        {others.length === 0 ? null : (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => {
              setCompareTo(pickAgainst(others, rm.version_no));
              setScreen('compare');
            }}
          >
            Сравнить версии
          </button>
        )}
      </>
    );
  };

  /**
   * Пост и полоса визуала — ОДНА колонка и одна зона сброса файла. «Картинка показывается
   * под постом» из требования Ильи — это буквально место в вёрстке, и оно одно во всех трёх
   * режимах экрана.
   *
   * ЗОНА СБРОСА — ВСЯ КОЛОНКА, а не пузырь сообщения. Внутри `.tg` фирменных токенов нет по
   * правилу ui-design §6, и подсветка там завела бы вторую нашу аннотацию поверх чужого
   * клиента; к тому же у поста без картинки пузырь мал, а промахнуться мимо него файлом
   * легко. Колонка включает пузырь, поэтому сброс на пост тоже срабатывает.
   */
  const visual = visualViewOf(rm);
  const proposedShown = showProposal ? proposedMediaOf(visual) : null;

  /**
   * «Перевести в расширенное» (§9.3) — ровно у версии, которая по политике уйдёт НЕ так, как
   * решено в «Настройках»: политика известна и включена, версия подписана обычным сообщением,
   * её ещё можно заменить. Пока на экране набранная правка или кандидат визуала, превью
   * показывает НЕ сохранённую форму — предлагать перевод сохранённой под ней значило бы
   * спорить с тем, что человек видит.
   */
  const convertOffer =
    policyRich === true &&
    rm.canonical.method === 'sendMessage' &&
    liveTyped === null &&
    proposedShown === null &&
    SUPERSEDABLE.has(rm.status);

  const postColumn = (): JSX.Element => (
    <div
      className={styles['column']}
      data-dropping={upload.dragging ? '' : undefined}
      data-testid="post-column"
      {...upload.dropProps}
    >
      <PreviewPanel
        model={rm}
        typed={liveTyped}
        proposed={proposedShown}
        proposedRef={showProposal ? proposedRefOf(visual) : null}
        richAlways={policyRich}
        cardAction={
          convertOffer ? <RichConvertAction model={rm} onConverted={onVersionCreated} /> : null
        }
      />
      <VisualStrip
        model={rm}
        upload={upload}
        showProposal={showProposal}
        onShowProposal={setShowProposal}
        onChanged={refetchAll}
      />
      {/* Файл — последний блок поста, поэтому и его полоса последняя из блоков: под полосой
          визуала. Мнение автора — не блок сообщения, а правка текста, и его полоса идёт после
          них: полоса визуала стоит непосредственно под превью (docs/ui-design.md §5.2). */}
      <DocumentStrip model={rm} upload={documentUpload} />
      <OpinionStrip model={rm} onChanged={onOpinionRequested} />
    </div>
  );

  const body = (): JSX.Element => {
    if (screen === 'edit') {
      return (
        <div className={`${styles['content']} ${styles['wide']}`}>
          {/* Тема остаётся и в правке: текст пишется по первоисточнику, и убирать ссылку на
              него в тот момент, когда его переписывают, значит гнать человека из режима. */}
          {rm.topic === null ? null : (
            <TopicPanel topic={rm.topic} formatRationale={rm.format_rationale} />
          )}
          <div className={styles['columns']}>
            <div className={styles['column']}>
              <SourceEditorPanel
                model={rm}
                onChanged={refetchAll}
                onTypedChange={setTyped}
                richAlways={policyRich}
              />
            </div>
            {postColumn()}
          </div>
        </div>
      );
    }

    if (screen === 'compare') {
      return (
        <div className={`${styles['content']} ${styles['wide']}`}>
          {others.length < 2 ? null : (
            <div className={styles['versions']}>
              <span className="micro">Сравнить с версией:</span>
              {others.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={styles['versionBtn']}
                  aria-pressed={compareTo === n}
                  onClick={() => {
                    setCompareTo(n);
                  }}
                >
                  v{n}
                </button>
              ))}
            </div>
          )}
          {diff.error !== undefined || baseModel.error !== undefined ? (
            // Тот же разбор, что и у версии: без этой ветки отказ сравнения выглядит как
            // бесконечное «Считаем различия…» (F-087).
            <div className="val val-err">
              Различия не посчитались: {(diff.error ?? baseModel.error)?.message}
            </div>
          ) : diff.data === undefined || parsedBase === null ? (
            <div className="micro">Считаем различия…</div>
          ) : (
            <VersionDiff result={diff.data} base={parsedBase} target={rm} />
          )}
          {/* Полоса стоит и здесь: решение по картинке принимают в любом режиме экрана, а
              состояние, видимое в двух режимах из трёх, читается как случайно пропавшее.
              Зона сброса здесь — сама полоса: полотен сравнения два, и «уроните на пост» в
              этом режиме не отвечает ни на один вопрос. */}
          <div
            className={styles['column']}
            data-dropping={upload.dragging ? '' : undefined}
            {...upload.dropProps}
          >
            <VisualStrip
              model={rm}
              upload={upload}
              showProposal={showProposal}
              onShowProposal={setShowProposal}
              onChanged={refetchAll}
            />
            <DocumentStrip model={rm} upload={documentUpload} />
            {/* Мнение — и в сравнении: по разнице версий видно, куда сервис его вставил. */}
            <OpinionStrip model={rm} onChanged={onOpinionRequested} />
          </div>
        </div>
      );
    }

    return (
      /*
       * Провенанс идёт ПЕРЕД превью: вопрос «откуда это взялось» предшествует и тексту, и
       * решению — при плохом ответе на него читать остальное незачем. На узком экране это
       * порядок сверху вниз, на широком — слева направо: `.reading` раскладывает тему и
       * превью в две колонки, как только для них хватает места.
       *
       * ПОЧЕМУ НЕ ПРОСТО РАСТЯНУТЬ ПРЕВЬЮ НА ВСЮ ШИРИНУ. Превью изображает сообщение
       * Telegram, и его ширина — часть изображаемого: растянутое на 1400px, оно показало бы
       * перенос строк, которого у подписчика не будет. Поэтому пустоту на широком экране
       * занимает ТЕМА, а не раздутое превью.
       */
      <div className={`${styles['content']} ${styles['reading']}`}>
        {rm.topic === null ? null : (
          <TopicPanel topic={rm.topic} formatRationale={rm.format_rationale} />
        )}
        {postColumn()}
      </div>
    );
  };

  return (
    <div className={styles['panel']}>
      {/*
        ШАПКА НАЗЫВАЕТ ПОСТ И ЕГО СОСТОЯНИЕ — И БОЛЬШЕ НИЧЕГО (правка 3.2 от 08.09.2026).
        Раньше здесь одной строкой печатались UUID целиком, `origin` СЫРЫМ КОДОМ («writer»),
        «версия N из M» и сырой ISO, а под заголовком висело «Почему такой формат: …».
        Что с чем стало:
          • «Черновик: news» → «post #N» (10.09.2026; до того — «news #<8 знаков UUID>»): ни
            слово «черновик», ни код формата не отвечают на «который это пост» — формат виден
            чипом в панели темы, а состояние называет пилюля справа;
          • `origin` и сырой ISO убраны. Код роли ничего не значит для согласующего, а
            неформатированный ISO был одним из двух мест, где UI печатал момент как есть;
          • «версия N из M» рисуется в строке `.meta` только когда версий больше одной: у поста
            с одной версией она сообщает, что выбора нет;
          • обоснование формата переехало `title`-подсказкой на чип формата в панели темы —
            подпись стоит рядом с величиной, которую объясняет.
        `versions`/`others` при этом остались: на них держится «Сравнить версии».

        СТИЛЬ — ВТОРАЯ ВЕЛИЧИНА ТОЙ ЖЕ СТРОКИ, по тому же правилу, что в бэклоге
        (docs/release-2026-09-19.md §5.5): «стиль v6» только у копии, «A · v5» / «B · v6» у пары
        сравнения, ничего у релиза. Пара ведёт на экран «бок о бок» кнопкой рядом с пилюлей:
        вторую половину пары из экрана одного черновика иначе не найти.

        ПОМЕТКА «АВТО» (релиз 2026-10-14) — рядом с пилюлей, как в строке бэклога: пост заказало
        расписание, а не человек. Признак приходит пропом из бэклога (см. `DraftPanelProps.auto`).

        СЛЕД АВТООДОБРЕНИЯ (2026-10-25, docs/ai-lab.md) — следом: «автоодобрен» или «не
        автоодобрен». Берётся из модели ПОКАЗАННОЙ версии, а не из бэклога: пилюля говорит о той
        версии, на которую человек смотрит. Причину пропуска экран пишет и строкой под шапкой —
        это данные, по которым человек решает сам, а не объяснение для ⓘ.
      */}
      <div className={styles['head']}>
        <div className={styles['headline']}>
          <h1 className="h1" title={head.draft_id}>
            {label}
          </h1>
          <StatusPill status={rm.status} />
          {auto ? <AutoBadge /> : null}
          {rm.auto_approval === undefined ? null : (
            <AutoApprovalBadge approval={rm.auto_approval} />
          )}
          {rm.comparison === null ? null : (
            <Link
              href={`/b/${encodeURIComponent(brand)}/compare/${encodeURIComponent(rm.comparison.request_id)}`}
              className="btn btn-ghost btn-sm"
              title="Открыть пару сравнения стилей бок о бок"
            >
              Сравнить
            </Link>
          )}
        </div>
        {headMeta.length === 0 ? null : <div className="meta">{headMeta.join(' · ')}</div>}
        {rm.auto_approval?.state === 'skipped' ? (
          <div className="meta" data-testid="auto-approval-skipped">
            {`автоодобрение пропустило версию: ${rm.auto_approval.reason ?? 'причина не записана'} — решение за человеком`}
          </div>
        ) : null}
      </div>

      {body()}

      {current === null ? (
        <div className="val val-warn">
          У черновика нет текущей версии — это состояние аномально.
        </div>
      ) : null}

      {/* Фрагмент из двух блоков: «что известно» встаёт в поток, полоса действий — подвалом,
          закреплённым у нижнего края окна. Обёртки вокруг него быть не должно: она стала бы
          областью прилипания и полоса уехала бы вместе с ней. См. шапку DecisionPanel.tsx. */}
      {/* Исход тестовой отправки перечитывает черновик и модель, не трогая набранную правку
          (`reread`): отказ «версия уже не текущая» обязан увести экран на текущую версию, а
          это видно только из списка версий черновика (ревью, ui-12). */}
      {/* `screenBrand` — бренд адреса: после перехода выше и сверки `rm.brand` он равен бренду
          черновика, и бэкенд сверяет его с брендом строки черновика (`expected_brand`). */}
      <DecisionPanel
        model={rm}
        screenBrand={brand}
        issues={issues}
        onChanged={refetchAll}
        onRefresh={reread}
        richAlways={policyRich}
        leading={modeControls()}
      />
    </div>
  );
}
